/**
 * The built-in beam (defs/beam.ts) as a data spec: a LINE run drawn like a
 * wall (two clicks between any two points), outlined by a dashed rectangle —
 * the plan convention for an overhead element — with the centerline shown
 * only while the beam is selected (`layer: 'selection'`).
 *
 * The code dashes EACH EDGE on its own: n dashes and n − 1 gaps of 8 cm,
 * the dashes stretched from their nominal 12 cm so every edge starts and
 * ends on a dash (n = round((length + gap) / (dash + gap)), at least 1).
 * A shape's `dashed` style runs one continuous pattern around the outline
 * instead, so this spec draws the dashes itself: one `index` repeat per edge,
 * copy `i` being dash `i` of that edge. `height` / `bottom elevation` are the
 * vertical bounds kept by the floor→layout conversion (`beamHeight` because
 * `height` is a reserved name in specs); neither affects the 2D glyph.
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type ObjectPolylineShape } from '../spec';

const W = 2.0;
const D = 0.2;

/** One dash of an edge: from `start + i · (dash + gap)` for `dash`, along u (at n = `at`) or along n (at u = `at`). */
function dash(id: string, name: string, axis: 'u' | 'n', at: string, start: string, dashLen: string): ObjectPolylineShape {
  const from = `${start} + i * (${dashLen} + gapLength)`;
  const to = `${from} + ${dashLen}`;
  const expr = axis === 'u' ? { 'p0.u': from, 'p0.n': at, 'p1.u': to, 'p1.n': at } : { 'p0.u': at, 'p0.n': from, 'p1.u': at, 'p1.n': to };
  return {
    id,
    name,
    kind: 'polyline',
    points: [
      [0, 0],
      [0, 0],
    ],
    closed: false,
    expr,
  };
}

export const beamSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'LINE',
  constraint: { kind: 'FREE' },
  drawLikeWall: true,
  defaultSize: { width: W, depth: D, height: 0.3 },
  params: [
    { key: 'beamHeight', kind: 'float', label: 'height', default: 0.3, min: 0, decimals: 2, distance: true },
    { key: 'elevation', kind: 'float', label: 'bottom elevation', default: 2.2, min: 0, decimals: 2, distance: true },
  ],
  formulas: [
    { key: 'dashLength', label: 'Dash length', expr: '0.12', note: 'Nominal dash length (m)' },
    { key: 'gapLength', label: 'Gap length', expr: '0.08', note: 'Gap between dashes (m)' },
    { key: 'lengthDashes', label: 'Dashes on each long edge', expr: 'max(1, round((width + gapLength) / (dashLength + gapLength)))', note: 'Dashes on each long edge' },
    { key: 'lengthDash', label: 'Long edge dash length', expr: '(width - (lengthDashes - 1) * gapLength) / lengthDashes', note: 'Long-edge dash, stretched so the edge starts and ends on a dash' },
    { key: 'endDashes', label: 'Dashes on each end', expr: 'max(1, round((depth + gapLength) / (dashLength + gapLength)))', note: 'Dashes on each end edge' },
    { key: 'endDash', label: 'End edge dash length', expr: '(depth - (endDashes - 1) * gapLength) / endDashes', note: 'End-edge dash, stretched so the edge starts and ends on a dash' },
  ],
  shapes: [
    dash('frontEdgeDash', 'Front edge dash', 'u', 'depth / 2', '-width / 2', 'lengthDash'),
    dash('backEdgeDash', 'Back edge dash', 'u', '-depth / 2', '-width / 2', 'lengthDash'),
    dash('startEdgeDash', 'Start edge dash', 'n', '-width / 2', '-depth / 2', 'endDash'),
    dash('endEdgeDash', 'End edge dash', 'n', 'width / 2', '-depth / 2', 'endDash'),
    {
      id: 'centerline',
      name: 'Centerline',
      kind: 'polyline',
      points: [
        [-W / 2, 0],
        [W / 2, 0],
      ],
      closed: false,
      layer: 'selection',
    },
  ],
  groups: [
    { id: 'front', name: 'Front edge', shapeIds: ['frontEdgeDash'], resize: { u: 'fixed', n: 'fixed' }, repeat: { kind: 'index', count: { expr: 'lengthDashes' } } },
    { id: 'back', name: 'Back edge', shapeIds: ['backEdgeDash'], resize: { u: 'fixed', n: 'fixed' }, repeat: { kind: 'index', count: { expr: 'lengthDashes' } } },
    { id: 'start', name: 'Start edge', shapeIds: ['startEdgeDash'], resize: { u: 'fixed', n: 'fixed' }, repeat: { kind: 'index', count: { expr: 'endDashes' } } },
    { id: 'end', name: 'End edge', shapeIds: ['endEdgeDash'], resize: { u: 'fixed', n: 'fixed' }, repeat: { kind: 'index', count: { expr: 'endDashes' } } },
    { id: 'axis', name: 'Centerline', shapeIds: ['centerline'], resize: { u: 'stretch', n: 'stretch' } },
  ],
  model3d: null,
};
